import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { BookOpen as BookOpenIcon, ChevronDown as ChevronDownIcon, ChevronUp as ChevronUpIcon } from 'lucide-react';

/**
 * CollapsibleSources - Renders RAG sources with collapse/expand functionality
 *
 * If there are more than `collapsedLimit` sources, shows only the first few
 * with a "..." button to expand and see all sources.
 */
export default function CollapsibleSources({ sources, collapsedLimit = 2 }) {
  const intl = useIntl();
  const [isExpanded, setIsExpanded] = useState(false);

  if (!sources || sources.length === 0) {
    return null;
  }

  // Deduplicate sources based on title (or string value)
  const uniqueSources = sources.reduce((acc, source) => {
    const key = source.title || source;
    if (!acc.seen.has(key)) {
      acc.seen.add(key);
      acc.result.push(source);
    }
    return acc;
  }, { seen: new Set(), result: [] }).result;

  const shouldCollapse = uniqueSources.length > collapsedLimit;
  const visibleSources = shouldCollapse && !isExpanded
    ? uniqueSources.slice(0, collapsedLimit)
    : uniqueSources;
  const hiddenCount = uniqueSources.length - collapsedLimit;

  const renderSource = (source, idx) => {
    const title = source.title || source;

    if (source.url) {
      return (
        <a
          key={idx}
          href={source.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-succession-bg-tertiary text-succession-secondary border border-succession-border/50 hover:bg-succession-secondary/10 transition-colors cursor-pointer"
        >
          {title}
        </a>
      );
    }

    return (
      <span
        key={idx}
        className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-succession-bg-tertiary text-succession-text-secondary border border-succession-border/50"
      >
        {title}
      </span>
    );
  };

  return (
    <div className="mt-3 pt-3 border-t border-succession-border/50">
      <div className="flex items-center gap-1.5 text-xs text-succession-text-muted mb-2">
        <BookOpenIcon className="h-3.5 w-3.5" />
        <span className="font-medium">{intl.formatMessage({ id: 'assistant.sources.label' })}</span>
      </div>
      <div className="flex flex-wrap gap-1.5 items-center">
        {visibleSources.map((source, idx) => renderSource(source, idx))}

        {shouldCollapse && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-succession-bg-secondary text-succession-text-muted border border-succession-border/50 hover:bg-succession-bg-tertiary hover:text-succession-text-secondary transition-colors cursor-pointer"
            aria-expanded={isExpanded}
            aria-label={isExpanded ? intl.formatMessage({ id: 'assistant.sources.aria_show_fewer' }) : intl.formatMessage({ id: 'assistant.sources.aria_show_more' }, { count: hiddenCount })}
          >
            {isExpanded ? (
              <>
                <span>{intl.formatMessage({ id: 'assistant.sources.show_less' })}</span>
                <ChevronUpIcon className="h-3 w-3" />
              </>
            ) : (
              <>
                <span>{intl.formatMessage({ id: 'assistant.sources.show_more' }, { count: hiddenCount })}</span>
                <ChevronDownIcon className="h-3 w-3" />
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}
